import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { alpacaService, WatchlistItem, SmartScanResult } from '../../services/AlpacaService';
import TICKER_DESC from '../../data/tickerDescriptions';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import styles from './Watchlist.module.css';

const Watchlist: React.FC = () => {
  const navigate = useNavigate();
  const [watchlists, setWatchlists] = useState<WatchlistItem[]>([]);
  const [activeList, setActiveList] = useState('');
  const [scanResults, setScanResults] = useState<SmartScanResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanLoading, setScanLoading] = useState(false);
  const [error, setError] = useState('');

  // Symbol management
  const [newSymbol, setNewSymbol] = useState('');

  // Create new list
  const [showCreate, setShowCreate] = useState(false);
  const [newListName, setNewListName] = useState('');

  const fetchWatchlists = useCallback(async () => {
    try {
      const data = await alpacaService.getWatchlists();
      const lists = data?.watchlists || [];
      setWatchlists(lists);
      if (lists.length > 0 && !activeList) {
        setActiveList(lists[0].name);
      }
    } catch (err: any) {
      // API may not be deployed yet — show empty state, not an error
      console.warn('Watchlist fetch failed:', err.message);
      setWatchlists([]);
    } finally {
      setLoading(false);
    }
  }, [activeList]);

  const fetchScanData = useCallback(async (symbols: string[]) => {
    if (symbols.length === 0) {
      setScanResults([]);
      return;
    }
    setScanLoading(true);
    try {
      const data = await alpacaService.smartScan(symbols.join(','), {});
      setScanResults(data.results || []);
    } catch {
      setScanResults([]);
    } finally {
      setScanLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWatchlists();
  }, []);

  // Fetch scan data when active list changes
  const currentList = watchlists.find((w) => w.name === activeList);
  useEffect(() => {
    if (currentList?.symbols?.length) {
      fetchScanData(currentList.symbols);
      // Auto-refresh every 30s
      const interval = setInterval(() => fetchScanData(currentList.symbols), 30000);
      return () => clearInterval(interval);
    } else {
      setScanResults([]);
    }
  }, [activeList, currentList?.symbols?.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    try {
      await alpacaService.saveWatchlist(newListName.trim(), []);
      setActiveList(newListName.trim());
      setNewListName('');
      setShowCreate(false);
      await fetchWatchlists();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteList = async () => {
    if (!activeList || !window.confirm(`Delete "${activeList}"?`)) return;
    try {
      await alpacaService.deleteWatchlist(activeList);
      setActiveList('');
      await fetchWatchlists();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddSymbol = async () => {
    const sym = newSymbol.trim().toUpperCase();
    if (!sym || !currentList) return;
    if (currentList.symbols.includes(sym)) {
      setNewSymbol('');
      return;
    }
    const updated = [...currentList.symbols, sym];
    try {
      await alpacaService.saveWatchlist(activeList, updated);
      setNewSymbol('');
      await fetchWatchlists();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRemoveSymbol = async (sym: string) => {
    if (!currentList) return;
    const updated = currentList.symbols.filter((s) => s !== sym);
    try {
      await alpacaService.saveWatchlist(activeList, updated);
      await fetchWatchlists();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const fmt = (v: number | null, d = 2) =>
    v != null ? v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';

  const signalClass = (signal: string) => {
    switch (signal) {
      case 'OVERSOLD':
        return styles.signalOversold;
      case 'OVERBOUGHT':
        return styles.signalOverbought;
      case 'WEAK':
        return styles.signalWeak;
      case 'STRONG':
        return styles.signalStrong;
      default:
        return styles.signalNeutral;
    }
  };

  if (loading)
    return (
      <div className={styles.page}>
        <SignalLoader message="Loading watchlists..." fullPage />
      </div>
    );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Watchlist</h1>
        <div className={styles.headerActions}>
          {watchlists.length > 0 && (
            <select
              className={styles.listSelect}
              value={activeList}
              onChange={(e) => setActiveList(e.target.value)}
              title="Select watchlist"
            >
              {watchlists.map((w) => (
                <option key={w.name} value={w.name}>
                  {w.name} ({w.symbols.length})
                </option>
              ))}
            </select>
          )}
          <button className={styles.btnNew} onClick={() => setShowCreate(true)}>
            + New List
          </button>
          {activeList && (
            <button className={styles.btnDanger} onClick={handleDeleteList}>
              Delete
            </button>
          )}
        </div>
      </div>

      {error && <p style={{ color: 'var(--color-negative)', marginBottom: 12 }}>{error}</p>}

      {showCreate && (
        <div className={styles.modal}>
          <input
            className={styles.modalInput}
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateList()}
            placeholder="List name (e.g. Tech, Energy, My Shorts)"
            autoFocus
          />
          <button className={styles.btnSave} onClick={handleCreateList}>
            Create
          </button>
          <button
            className={styles.btnCancel}
            onClick={() => {
              setShowCreate(false);
              setNewListName('');
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {watchlists.length === 0 && !showCreate && (
        <div className={styles.empty}>
          No watchlists yet. Click <strong>+ New List</strong> to create your first one.
        </div>
      )}

      {currentList && (
        <>
          {/* Symbol management */}
          <div className={styles.symbolBar}>
            <input
              className={styles.symbolInput}
              value={newSymbol}
              onChange={(e) => setNewSymbol(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleAddSymbol()}
              placeholder="Add symbol (e.g. NVDA)"
              list="wl-symbols"
            />
            <button className={styles.btnAdd} onClick={handleAddSymbol}>
              Add
            </button>
            <datalist id="wl-symbols">
              {Object.keys(TICKER_DESC)
                .filter((s) => !currentList.symbols.includes(s))
                .slice(0, 20)
                .map((s) => (
                  <option key={s} value={s} />
                ))}
            </datalist>
          </div>

          {currentList.symbols.length > 0 && (
            <div className={styles.symbolChips}>
              {currentList.symbols.map((sym) => (
                <span key={sym} className={styles.chip}>
                  {sym}
                  <button className={styles.chipRemove} onClick={() => handleRemoveSymbol(sym)} title="Remove">
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Indicator table */}
          {scanLoading && scanResults.length === 0 && <SignalLoader message="Scanning indicators..." />}

          {scanResults.length > 0 && (
            <>
              <p className={styles.refreshInfo}>Auto-refreshes every 30s during market hours</p>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th>Price</th>
                      <th>Change</th>
                      <th>RSI(2)</th>
                      <th>RSI(6)</th>
                      <th>RSI(14)</th>
                      <th>Signal</th>
                      <th>Direction</th>
                      <th>Rel Vol</th>
                      <th>VWAP</th>
                      <th>MACD</th>
                      <th>Score</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {scanResults.map((r) => (
                      <tr key={r.symbol}>
                        <td
                          className={styles.symbol}
                          onClick={() => navigate(`/market?symbol=${r.symbol}`)}
                          title={TICKER_DESC[r.symbol] || r.symbol}
                        >
                          {r.symbol}
                        </td>
                        <td>${fmt(r.price)}</td>
                        <td className={r.change_pct >= 0 ? styles.positive : styles.negative}>
                          {r.change_pct >= 0 ? '+' : ''}
                          {fmt(r.change_pct, 1)}%
                        </td>
                        <td
                          className={
                            r.rsi2 != null && r.rsi2 <= 10
                              ? styles.positive
                              : r.rsi2 != null && r.rsi2 >= 90
                                ? styles.negative
                                : ''
                          }
                        >
                          {r.rsi2 != null ? fmt(r.rsi2, 1) : '—'}
                        </td>
                        <td
                          className={
                            r.rsi6 != null && r.rsi6 <= 20
                              ? styles.positive
                              : r.rsi6 != null && r.rsi6 >= 80
                                ? styles.negative
                                : ''
                          }
                        >
                          {r.rsi6 != null ? fmt(r.rsi6, 1) : '—'}
                        </td>
                        <td>{r.rsi14 != null ? fmt(r.rsi14, 1) : '—'}</td>
                        <td>
                          <span className={`${styles.signalBadge} ${signalClass(r.signal)}`}>{r.signal}</span>
                        </td>
                        <td
                          className={
                            r.direction === 'BULL'
                              ? styles.dirBull
                              : r.direction === 'BEAR'
                                ? styles.dirBear
                                : styles.dirChop
                          }
                        >
                          {r.direction}
                        </td>
                        <td className={r.rel_vol != null && r.rel_vol >= 2 ? styles.positive : ''}>
                          {r.rel_vol != null ? `${fmt(r.rel_vol, 1)}x` : '—'}
                        </td>
                        <td
                          className={
                            r.vwap_position === 'above'
                              ? styles.positive
                              : r.vwap_position === 'below'
                                ? styles.negative
                                : ''
                          }
                        >
                          {r.vwap_position || '—'}
                        </td>
                        <td
                          className={
                            r.macd_signal === 'bullish'
                              ? styles.positive
                              : r.macd_signal === 'bearish'
                                ? styles.negative
                                : ''
                          }
                        >
                          {r.macd_signal || '—'}
                        </td>
                        <td
                          className={
                            r.composite_score > 0 ? styles.positive : r.composite_score < 0 ? styles.negative : ''
                          }
                        >
                          {fmt(r.composite_score, 1)}
                        </td>
                        <td>
                          <button
                            className={styles.tradeBtn}
                            onClick={() => navigate(`/quick-trade?symbol=${r.symbol}`)}
                          >
                            Trade
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {currentList.symbols.length > 0 && scanResults.length === 0 && !scanLoading && (
            <div className={styles.empty}>No scan data available. Market may be closed.</div>
          )}
        </>
      )}
    </div>
  );
};

export default Watchlist;
